/**
 * Bank-feed auto-categorization — Wave F.
 *
 * Pure helper that suggests a GL account + (optional vendor) for an unmatched
 * BankTxn, layering three signals in priority order:
 *
 *   1. Vendor memory (highest confidence): if the txn description contains a
 *      known vendor name, surface that vendor's preferred categorization
 *      account from `suggestVendorAccount`.
 *   2. Description keyword rules: deterministic fallback for txns the books
 *      have never seen before ("AMEX EPAYMENT" → CC payment, "SHELL OIL" →
 *      Vehicles & Fuel, "STRIPE PAYOUT" → AR clearing, etc.).
 *   3. Side-based default: outflow → 6900 Other Operating Expense, inflow →
 *      4990 Misc Revenue. Never silently picks AR/AP control accounts.
 *
 * Returns `null` when no signal beats a 0.15 confidence floor — the UI should
 * leave the GL account empty rather than mis-categorize.
 *
 * Used by `BankReconciliationPage` to seed the "Post new JE" form for txns
 * that have no JE match.
 */
import type { JournalEntry } from "../glTypes";
import type { BankTxn } from "./bankFeedTypes";
import { suggestVendorAccount } from "../vendorMemory";

export interface BankCategorizationSuggestion {
  accountId: string;
  vendorId?: string;
  vendorName?: string;
  confidence: number; // 0..1
  source: "vendor_memory" | "keyword_rule" | "side_default";
  reason: string;
}

/** Vendor catalog signature — keep loose to avoid coupling to AP types. */
interface VendorLike { id: string; name: string }

interface KeywordRule {
  test: RegExp;
  accountId: string;
  label: string;
}

/** Deterministic, contractor-domain keyword rules. Ordered: first match wins. */
const KEYWORD_RULES: KeywordRule[] = [
  { test: /\b(amex|visa|mastercard|chase\s*card|capital\s*one)\s*(epayment|payment|pmt|autopay)\b/i, accountId: "2100", label: "CC payment" },
  { test: /\b(shell|chevron|exxon|bp|mobil|sunoco|wawa|7-?eleven|gas\b)/i, accountId: "6200", label: "Vehicles & Fuel" },
  { test: /\b(uber|lyft|delta|american\s*airlines|united\s*airlines|hotel|marriott|hilton)/i, accountId: "6600", label: "Travel / M&E" },
  { test: /\b(home\s*depot|lowe'?s|menards|ferguson|sherwin[-\s]?williams|harbor\s*freight)/i, accountId: "1200", label: "Materials → WIP" },
  { test: /\b(stripe|square|paypal|venmo)\s*(payout|deposit|transfer)/i, accountId: "1090", label: "Processor payout" },
  { test: /\b(google\s*ads|facebook|meta|yelp|angi|thumbtack|home\s*advisor)/i, accountId: "6300", label: "Marketing" },
  { test: /\b(quickbooks|intuit|salesforce|hubspot|microsoft|adobe|zoom|slack|aws|gcp|google\s*workspace)/i, accountId: "6500", label: "Software & Subscriptions" },
  { test: /\b(progressive|geico|state\s*farm|liberty\s*mutual|hartford|hiscox)\b.*\b(ins|insurance|premium)\b/i, accountId: "6400", label: "Insurance" },
  { test: /\b(verizon|att|t-?mobile|comcast|spectrum|cox|fpl|teco|duke\s*energy)/i, accountId: "6110", label: "Utilities" },
  { test: /\b(rent|lease)\b/i, accountId: "6100", label: "Rent" },
  { test: /\bnsf|overdraft|service\s*fee|wire\s*fee|monthly\s*fee\b/i, accountId: "6800", label: "Bank fee" },
  { test: /\b(payroll|gusto|adp|paychex)\b/i, accountId: "6000", label: "Payroll — Office" },
];

const VENDOR_DESCRIPTION_MIN_LEN = 4;

/** Try to find a vendor whose name appears in the bank description. */
function matchVendorInDescription(
  description: string,
  vendors: VendorLike[],
): VendorLike | null {
  const desc = description.toLowerCase();
  // Prefer longest-name match (more specific).
  const candidates = vendors
    .filter(v => v.name && v.name.length >= VENDOR_DESCRIPTION_MIN_LEN)
    .sort((a, b) => b.name.length - a.name.length);
  for (const v of candidates) {
    const needle = v.name.toLowerCase();
    if (desc.includes(needle)) return v;
    // Token-level fallback: every word in vendor name appears in desc.
    const tokens = needle.split(/\s+/).filter(t => t.length >= 4);
    if (tokens.length >= 2 && tokens.every(t => desc.includes(t))) return v;
  }
  return null;
}

export function suggestBankTxnCategorization(
  txn: BankTxn,
  vendors: VendorLike[],
  journalEntries: JournalEntry[],
): BankCategorizationSuggestion | null {
  // (1) Vendor memory.
  const vendor = matchVendorInDescription(txn.description, vendors);
  if (vendor) {
    const memory = suggestVendorAccount(vendor.id, journalEntries, txn.postedDate);
    if (memory && memory.confidence >= 0.4) {
      return {
        accountId: memory.accountId,
        vendorId: vendor.id,
        vendorName: vendor.name,
        confidence: Math.min(0.95, 0.55 + memory.confidence * 0.4),
        source: "vendor_memory",
        reason: `Vendor "${vendor.name}" — ${memory.reason}`,
      };
    }
  }

  // (2) Keyword rules.
  for (const rule of KEYWORD_RULES) {
    if (rule.test.test(txn.description)) {
      return {
        accountId: rule.accountId,
        vendorId: vendor?.id,
        vendorName: vendor?.name,
        confidence: 0.6,
        source: "keyword_rule",
        reason: `Matched "${rule.label}" pattern in description.`,
      };
    }
  }

  // (3) Side default — confidence below floor; caller may show as a faint hint.
  return {
    accountId: txn.side === "outflow" ? "6900" : "4990",
    confidence: 0.15,
    source: "side_default",
    reason: txn.side === "outflow"
      ? "No vendor or keyword match — defaulting to Other Operating Expense."
      : "No match — defaulting to Misc Revenue.",
  };
}
